import { getHighlightPermissions, hasRequiredPermissions, hasRequiredRoles, useCurrentUser } from '@repo/utils';
import { PermissionHighlightOverlay } from './PermissionHighlightOverlay';
import React, { useEffect, useState } from 'react';

/** Props for the PermissionGate wrapper component. */
export interface PermissionGateProps {
  /** Permission keys — by default the user needs at least ONE (OR logic); see `requireAll`. */
  permissions?: string[];
  /** Role names — by default the user needs at least ONE (OR logic); see `requireAll`. */
  roles?: string[];
  /**
   * When `true`, the user must satisfy EVERY listed permission (and every listed role, if roles
   * are also given) rather than just one. Defaults to `false` (existing OR behavior), so this is
   * fully backward-compatible with every existing call site. `permissions` and `roles` are still
   * evaluated as independent constraints — passing both requires satisfying both groups (ANDed
   * together), same as today.
   */
  requireAll?: boolean;
  /** Rendered instead of `children` when the user fails the check (and highlighting is off). */
  fallback?: React.ReactNode;
  children: React.ReactNode;
  /** Render the highlight wrapper as inline `span` instead of block `div`. */
  inline?: boolean;
}

/**
 * Declarative wrapper for gating arbitrary UI elements behind permissions/roles.
 * Renders `children` when the user passes the check; otherwise renders `fallback`
 * (or nothing). When the admin `highlightPermissions` mode is enabled, always
 * renders `children` wrapped in a `PermissionHighlightOverlay` so gated content
 * remains visible for auditing, regardless of pass/fail.
 *
 * If neither `permissions` nor `roles` is provided, always renders `children`.
 *
 * By default, `permissions`/`roles` each use OR logic (any one match passes) and the two groups
 * are OR'd together. Pass `requireAll` to instead require every listed permission (and every
 * listed role, if given) — e.g. gating a super-admin-only *and* feature-specific action:
 * `<PermissionGate permissions={[AppPermissions.prism.adminManage, AppPermissions.prism.dataScopeManage]} requireAll>`.
 */
export function PermissionGate({ permissions, roles, requireAll, fallback, children, inline }: PermissionGateProps) {
  const { userPermissionKeys, userRoles } = useCurrentUser();
  const [highlightPermissions, setHighlightPermissions] = useState(getHighlightPermissions);

  useEffect(() => {
    const handleHighlightChange = () => setHighlightPermissions(getHighlightPermissions());
    window.addEventListener('prism_highlight_permissions_changed', handleHighlightChange);
    return () => window.removeEventListener('prism_highlight_permissions_changed', handleHighlightChange);
  }, []);

  const hasPermConstraint = !!permissions?.length;
  const hasRoleConstraint = !!roles?.length;

  if (!hasPermConstraint && !hasRoleConstraint) {
    return <>{children}</>;
  }

  let passes: boolean;
  if (requireAll) {
    const passesPermAll = !hasPermConstraint || permissions!.every(p => userPermissionKeys.includes(p));
    const passesRoleAll = !hasRoleConstraint || roles!.every(r => userRoles.includes(r));
    passes = passesPermAll && passesRoleAll;
  } else {
    const passesPerm = hasPermConstraint && hasRequiredPermissions(permissions, userPermissionKeys);
    const passesRole = hasRoleConstraint && hasRequiredRoles(roles, userRoles);
    passes = passesPerm || passesRole;
  }

  if (passes || highlightPermissions) {
    return (
      <PermissionHighlightOverlay permissions={permissions} roles={roles} passes={passes} inline={inline}>
        {children}
      </PermissionHighlightOverlay>
    );
  }

  return <>{fallback ?? null}</>;
}
